import { NextWorkflowError } from 'models/NextWorkflowError';
import { Action } from 'state/actions';
import { DateFilter } from 'state/blotters/common/blotter.model';
import { onColumnSortChanged, onColumnStateChanged } from 'state/blotters/common/blotter.reducer.utils';
import { toLocaleDate, toLocaleDateOnlyString } from 'state/blotters/common/blotter.utils';
import { TradesBlottersFilterChanged, TradesBlottersItemsReceived } from 'state/blotters/trades/tradesBlotters.actions';
import {
  initialTradesBlottersState,
  TradesBlotters,
  TradesBlottersState,
} from 'state/blotters/trades/tradesBlotters.model';
import { ItemsBlotter, TradesBlottersByBlotterType } from 'state/blotters/trades/types/blotter';
import {
  TradesBlottersErrorByBlotterType,
  TradesBlottersItem,
  TradesBlottersItemByBlotterType,
} from 'state/blotters/trades/types/blotterItem';
import {
  AllTradeBlottersFiltersByBlotterType,
  TradesOrProductsBlotterFilters,
} from 'state/blotters/trades/types/filters';
import {
  BlotterType,
  FilterBlotterType,
  isMasterDetailBlotterType,
  TradesOrProductsBlotterType,
} from 'state/blotters/trades/types/types';
import { now } from 'utils/core/now';

type BlotterByType<T extends BlotterType> = Omit<TradesBlottersByBlotterType<T>, 'columnDefinition'>;

export function tradesBlottersReducer(
  state: TradesBlottersState = initialTradesBlottersState,
  action: Action,
): TradesBlottersState {
  switch (action.type) {
    case 'TRADES_BLOTTERS_COLUMN_DEFINITION_RECEIVED':
      return {
        ...state,
        blotters: {
          ...state.blotters,
          [action.blotter]: {
            ...state.blotters[action.blotter],
            columnDefinition: action.columnDefinition,
            sort: [],
          } as TradesBlotters[typeof action.blotter],
        },
      };
    case 'TRADES_BLOTTERS_GLOBAL_FILTERS_LOADED':
      return {
        ...state,
        filters: { ...action.filters, ...state.filters },
      };
    case 'TRADES_BLOTTERS_FILTERS_LOADED':
      return {
        ...state,
        blotters: {
          ...state.blotters,
          [action.blotter]: {
            ...state.blotters[action.blotter],
            filters: { ...state.blotters[action.blotter].filters, ...action.filters },
          } satisfies BlotterByType<typeof action.blotter>,
        },
      };
    case 'TRADES_BLOTTERS_ITEMS_PER_PAGE_CHANGED':
      return {
        ...state,
        blotters: {
          ...state.blotters,
          [action.blotter]: {
            ...state.blotters[action.blotter],
            blotterPreferenceState: {
              ...state.blotters[action.blotter].blotterPreferenceState,
              pageSizeState: action.itemsPerPage,
            },
          } satisfies BlotterByType<typeof action.blotter>,
        },
      };
    case 'TRADES_BLOTTERS_FILTER_CHANGED':
      return {
        ...state,
        blotters: { ...state.blotters, [action.blotter]: onFilterChanged(state.blotters[action.blotter], action) },
      };
    case 'TRADES_BLOTTERS_FILTER_ENABLED':
      return { ...state, filters: { blotterType: action.blotter } };
    case 'TRADES_BLOTTERS_ADVANCED_FILTERS_TOGGLED':
      return {
        ...state,
        blotters: {
          ...state.blotters,
          [action.blotter]: {
            ...state.blotters[action.blotter],
            filters: {
              ...state.blotters[action.blotter].filters,
              advancedFilters: !state.blotters[action.blotter].filters.advancedFilters,
            },
          } satisfies BlotterByType<typeof action.blotter>,
        },
      };
    case 'TRADES_BLOTTERS_ITEMS_RECEIVED':
      if (isMasterDetailBlotterType(action.blotter)) {
        return {
          ...state,
          blotters: {
            ...state.blotters,
            [action.blotter]: {
              ...state.blotters[action.blotter],
              innerBlotters: {
                ...state.blotters[action.blotter].innerBlotters,
                [action.parentId as string]: onItemsReceived<
                  TradesBlottersItemByBlotterType<typeof action.blotter>,
                  TradesBlottersErrorByBlotterType<typeof action.blotter>
                >(state.blotters[action.blotter].innerBlotters[action.parentId as string], action),
              },
            },
          },
        };
      }
      return {
        ...state,
        blotters: {
          ...state.blotters,
          [action.blotter]: onItemsReceived<
            TradesBlottersItemByBlotterType<typeof action.blotter>,
            TradesBlottersErrorByBlotterType<typeof action.blotter>
          >(state.blotters[action.blotter], action),
        },
      };
    case 'TRADES_BLOTTERS_ITEMS_REQUESTED':
      if (isMasterDetailBlotterType(action.blotter)) {
        return {
          ...state,
          blotters: {
            ...state.blotters,
            [action.blotter]: {
              ...state.blotters[action.blotter],
              innerBlotters: {
                ...state.blotters[action.blotter].innerBlotters,
                [action.request as string]: {
                  ...state.blotters[action.blotter].innerBlotters[action.request as string],
                  error: false,
                  loading: true,
                },
              },
            },
          },
        };
      }
      return {
        ...state,
        blotters: {
          ...state.blotters,
          [action.blotter]: {
            ...state.blotters[action.blotter],
            error: false,
            loading: true,
          } satisfies BlotterByType<typeof action.blotter>,
        },
      };
    case 'TRADES_BLOTTERS_PREFERENCE_STATE_GET':
      return {
        ...state,
        blotters: {
          ...state.blotters,
          [action.blotter]: {
            ...state.blotters[action.blotter],
            areBlotterPreferencesInError: false,
          } satisfies BlotterByType<typeof action.blotter>,
        },
      };
    case 'TRADES_BLOTTERS_PREFERENCE_STATE_LOADED':
      return {
        ...state,
        blotters: {
          ...state.blotters,
          [action.blotter]: {
            ...state.blotters[action.blotter],
            areBlotterPreferencesInError: false,
            blotterPreferenceState: action.blotterPreferenceState,
            canSaveBlotterPreferences: true,
          } satisfies BlotterByType<typeof action.blotter>,
        },
      };
    case 'TRADES_BLOTTERS_PREFERENCE_STATE_LOADING_FAILED':
      return {
        ...state,
        blotters: {
          ...state.blotters,
          [action.blotter]: {
            ...state.blotters[action.blotter],
            areBlotterPreferencesInError: true,
            canSaveBlotterPreferences: false,
            error: true,
            loading: false,
          } satisfies BlotterByType<typeof action.blotter>,
        },
      };
    case 'TRADES_BLOTTERS_COLUMN_STATE_CHANGED':
      return {
        ...state,
        blotters: {
          ...state.blotters,
          [action.blotter]: onColumnStateChanged(state.blotters[action.blotter], action) satisfies BlotterByType<
            typeof action.blotter
          >,
        },
      };
    case 'TRADES_BLOTTERS_SORT_CHANGED':
      return {
        ...state,
        blotters: {
          ...state.blotters,
          [action.blotter]: onColumnSortChanged(state.blotters[action.blotter], action) satisfies BlotterByType<
            typeof action.blotter
          >,
        },
      };
    case 'TRADES_BLOTTERS_CREDIT_CHECK_STATUS_UPDATED':
      return {
        ...state,
        blotters: {
          ...state.blotters,
          trades: {
            ...state.blotters.trades,
            creditCheckStatus: action.creditCheckStatus,
          },
        },
      };
    case 'TRADES_BLOTTERS_CREDIT_CHECK_STATUS_REQUEST':
      return {
        ...state,
        blotters: {
          ...state.blotters,
          trades: {
            ...state.blotters.trades,
            creditCheckStatus: undefined,
          },
        },
      };
    case 'ROUTE_CHANGED':
      return action.type === 'ROUTE_CHANGED' && action.path === '/trade-blotter'
        ? {
            ...state,
            blotters: {
              ...state.blotters,
              trades: {
                ...state.blotters.trades,
                creditCheckStatus: undefined,
              },
            },
          }
        : state;

    case 'TRADES_BLOTTERS_ITEMS_FETCHING_FAILED':
      if (isMasterDetailBlotterType(action.blotter)) {
        return {
          ...state,
          blotters: {
            ...state.blotters,
            [action.blotter]: {
              ...state.blotters[action.blotter],
              innerBlotters: {
                ...state.blotters[action.blotter].innerBlotters,
                [action.parentId as string]: {
                  ...state.blotters[action.blotter].innerBlotters[action.parentId as string],
                  error: true,
                  errorContent: action.error,
                  loading: false,
                },
              },
            } as TradesBlottersByBlotterType<typeof action.blotter>,
          },
        };
      }
      return {
        ...state,
        blotters: {
          ...state.blotters,
          [action.blotter]: {
            ...state.blotters[action.blotter],
            error: true,
            loading: false,
          } satisfies BlotterByType<typeof action.blotter>,
        },
      };

    case 'TRADES_BLOTTERS_FILTERS_LOADING_FAILED':
    case 'TRADES_BLOTTERS_FETCH_COLUMN_DEFINITION_FAILED':
      return {
        ...state,
        blotters: {
          ...state.blotters,
          [action.blotter]: { ...state.blotters[action.blotter], error: true, loading: false } satisfies BlotterByType<
            typeof action.blotter
          >,
        },
      };
    case 'TRADES_BLOTTER_PLATFORMS_FETCHED':
      return { ...state, availablePlatforms: action.data, availablePlatformsFetchingStatus: 'success' };
    case 'TRADES_BLOTTER_PLATFORMS_FETCH_FAILED':
      return { ...state, availablePlatformsFetchingStatus: 'error' };
    case 'TRADES_BLOTTERS_SET_LOADING':
      if (isMasterDetailBlotterType(action.blotter)) {
        return {
          ...state,
          blotters: {
            ...state.blotters,
            [action.blotter]: {
              ...state.blotters[action.blotter],
              innerBlotters: {
                ...state.blotters[action.blotter].innerBlotters,
                [action.parentId as string]: {
                  ...state.blotters[action.blotter].innerBlotters[action.parentId as string],
                  loading: action.isLoading,
                },
              },
            },
          },
        };
      }
      return {
        ...state,
        blotters: {
          ...state.blotters,
          [action.blotter]: {
            ...state.blotters[action.blotter],
            loading: action.isLoading,
          } satisfies BlotterByType<typeof action.blotter>,
        },
      };
    default:
      return state;
  }
}

function onItemsReceived<T extends TradesBlottersItem, ErrorContent>(
  blotter: ItemsBlotter<T, ErrorContent>,
  action: TradesBlottersItemsReceived,
) {
  return {
    ...blotter,
    error: false,
    loading: false,
    items: action.items,
  } as ItemsBlotter<T, NextWorkflowError[]>;
}
function onFilterChanged<T extends FilterBlotterType>(
  state: BlotterByType<T>,
  action: TradesBlottersFilterChanged,
): BlotterByType<T> {
  const tradesOrProductsFilters = state.filters as AllTradeBlottersFiltersByBlotterType<'trades' | 'products'>;
  const tradesOrProductsAction = action as TradesBlottersFilterChanged<
    TradesOrProductsBlotterType,
    keyof TradesOrProductsBlotterFilters
  >;
  switch (tradesOrProductsAction.filter) {
    case 'dateFilter': {
      return {
        ...state,
        filters: {
          ...tradesOrProductsFilters,
          dateFilter: tradesOrProductsAction.value as DateFilter,
          startDate: undefined,
          endDate: undefined,
        } as AllTradeBlottersFiltersByBlotterType<typeof tradesOrProductsAction.blotter>,
      };
    }
    case 'startDate': {
      return {
        ...state,
        filters: {
          ...state.filters,
          startDate: tradesOrProductsAction.value as string,
          endDate: tradesOrProductsFilters.endDate || toLocaleDateOnlyString(new Date(now())),
        } as AllTradeBlottersFiltersByBlotterType<typeof tradesOrProductsAction.blotter>,
      };
    }
    case 'endDate': {
      return {
        ...state,
        filters: {
          ...tradesOrProductsFilters,
          endDate: tradesOrProductsAction.value as string,
          startDate:
            tradesOrProductsFilters.startDate ||
            toLocaleDateOnlyString(toLocaleDate(tradesOrProductsFilters.dateFilter)),
        } as AllTradeBlottersFiltersByBlotterType<typeof tradesOrProductsAction.blotter>,
      };
    }
  }

  return { ...state, filters: { ...state.filters, [action.filter]: action.value } };
}
